import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import type { EnvironmentDef } from '../config/environments';

/** Renderer settings, sky, sun and lights for an environment. Greybox until Phase 2 art. */
export class Stage {
  readonly sun: THREE.DirectionalLight;
  private readonly sunTarget = new THREE.Object3D();
  private readonly hemi = new THREE.HemisphereLight();

  constructor(
    readonly renderer: THREE.WebGLRenderer,
    readonly scene: THREE.Scene,
    env: EnvironmentDef,
  ) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;

    // A soft studio environment so metal (coins, trays, rims) has something to reflect. Built once.
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.55;
    pmrem.dispose();
    scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight();
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(1024, 1024);
    const cam = this.sun.shadow.camera;
    cam.left = -8;
    cam.right = 8;
    cam.top = 6;
    cam.bottom = -6;
    cam.near = 1;
    cam.far = 60;
    this.sun.target = this.sunTarget;
    scene.add(this.sun, this.sunTarget);
    this.apply(env);
  }

  /** Sky, fog, light colours and exposure for an environment (called again when switching). */
  apply(env: EnvironmentDef): void {
    const p = env.palette;
    (this.scene.background as THREE.Texture | null)?.dispose();
    this.scene.background = skyTexture(p.skyTop, p.skyHorizon);
    this.scene.fog = new THREE.Fog(p.skyHorizon, env.visual.fog[0], env.visual.fog[1]);
    this.hemi.color.set(p.skyHorizon);
    this.hemi.groundColor.set(p.groundDeep);
    this.hemi.intensity = 1.25;
    this.sun.color.set(p.sun);
    this.sun.intensity = env.visual.sunIntensity;
    this.renderer.toneMappingExposure = env.visual.exposure;
  }

  /** Low quality turns the sun's shadow map off (the contact shadow under the car stays). */
  setShadows(on: boolean): void {
    if (this.renderer.shadowMap.enabled === on) return;
    this.renderer.shadowMap.enabled = on;
    this.sun.castShadow = on;
    this.scene.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) m.needsUpdate = true;
    });
  }

  /** Keeps the tight shadow frustum centred on the buggy. */
  follow(x: number, y: number): void {
    // Nearly overhead (slightly from the front-left) so the car's shadow lands under it on the track.
    this.sun.position.set(x - 5, y + 28, 3.5);
    this.sunTarget.position.set(x, y, 0);
  }
}

function skyTexture(top: string, horizon: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable for sky gradient');
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, top);
  g.addColorStop(0.72, horizon);
  g.addColorStop(1, horizon);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 4, 256);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
